import type { Experiment } from '@/types'

// --- Helpers ---

function daysBetween(dateStr: string, now: Date): number {
  const d = new Date(dateStr)
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)))
}

// --- Per-experiment monthly impact projection ---

export interface ExperimentImpactProjection {
  projectedMonthlyGain: number
  liftAbsolute: number
  bestVariant: string
  treatmentAOV: number
  experimentMonthlySessions: number
  usedStoreAOVFallback: boolean
}

/**
 * Project monthly revenue impact for a single active experiment.
 * Uses the experiment's own session rate (not store-wide traffic) and the
 * best-performing non-control variant's AOV. Falls back to store-level AOV
 * when the variant has no orders yet.
 */
export function projectExperimentMonthlyGain(
  results: any,
  storeAOV: number = 0,
  isNegative: boolean = false
): ExperimentImpactProjection | null {
  if (!results?.summary) return null

  // `summary.lift` is the PRIMARY GOAL's lift. Multiplying a banner-click or cart-view lift by AOV
  // fabricates revenue (Naturalu "banner 3x2": +0.65pp clicks → "+$18.5M/mes"). Only a purchase
  // goal (or a legacy experiment without goal metadata) can be projected into money.
  if (results.summary.primary_metric?.kind === 'goal') return null

  const lift = results.summary.lift
  if (!lift) return null
  // `lift.absolute` is always treatment − control. In a negative experiment the HYPOTHESIS is the
  // control arm, so its gain is the negated difference and the projected arm is control.
  const hypothesisLiftAbs = isNegative ? -(lift.absolute ?? 0) : (lift.absolute ?? 0)
  if (!(hypothesisLiftAbs > 0)) return null

  const variants = (results.variants || {}) as Record<string, any>
  let bestVariant: string
  if (isNegative) {
    if (!variants.control) return null
    bestVariant = 'control'
  } else {
    const nonControlKeys = Object.keys(variants).filter(k => k !== 'control')
    if (nonControlKeys.length === 0) return null
    bestVariant = nonControlKeys[0]
    let bestRate = variants[bestVariant]?.conversion_rate ?? 0
    for (const k of nonControlKeys) {
      const r = variants[k]?.conversion_rate ?? 0
      if (r > bestRate) {
        bestRate = r
        bestVariant = k
      }
    }
  }

  let treatmentAOV = variants[bestVariant]?.average_order_value ?? 0
  let usedStoreAOVFallback = false
  if (treatmentAOV <= 0) {
    if (storeAOV > 0) {
      treatmentAOV = storeAOV
      usedStoreAOVFallback = true
    } else {
      return null
    }
  }

  const expSessions = results.summary?.total_sessions ??
    Object.values((results.sessions || {}) as Record<string, number>).reduce((a, b) => a + b, 0)
  const daysRunning = Math.max(results.duration?.days_running ?? 1, 1)
  const experimentMonthlySessions = (expSessions / daysRunning) * 30
  if (experimentMonthlySessions <= 0) return null

  return {
    projectedMonthlyGain: hypothesisLiftAbs * experimentMonthlySessions * treatmentAOV,
    liftAbsolute: hypothesisLiftAbs,
    bestVariant,
    treatmentAOV,
    experimentMonthlySessions,
    usedStoreAOVFallback,
  }
}

/**
 * Project monthly impact for a completed experiment from its frozen
 * results_summary. Mirrors the active-experiment math but uses the recorded
 * winner instead of recomputing the best variant.
 */
export function projectCompletedExperimentMonthlyGain(
  experiment: Experiment,
  storeAOV: number = 0
): ExperimentImpactProjection | null {
  const summary = experiment.results_summary
  if (!summary?.is_significant || !summary.winner) return null

  const winnerKey = summary.winner
  // Frozen `lift.absolute` is treatment − control; a control win (negative experiment's hypothesis,
  // or plain "control wins") is a negative number whose magnitude is the gain of keeping control.
  const rawLift = summary.lift?.absolute ?? 0
  const liftAbsolute = winnerKey === 'control' ? -rawLift : rawLift
  if (liftAbsolute <= 0) return null

  const variant = summary.variants?.[winnerKey]
  let treatmentAOV = variant && variant.conversions > 0 ? variant.revenue / variant.conversions : 0
  let usedStoreAOVFallback = false
  if (treatmentAOV <= 0) {
    if (storeAOV > 0) {
      treatmentAOV = storeAOV
      usedStoreAOVFallback = true
    } else {
      return null
    }
  }

  const expSessions = summary.total_sessions ?? 0
  const startStr = (experiment as any).timing?.start_date_time || experiment.created_at
  const endStr = experiment.finalized_at || experiment.updated_at
  let daysRunning = 1
  if (startStr && endStr) {
    daysRunning = Math.max(
      1,
      Math.floor((new Date(endStr).getTime() - new Date(startStr).getTime()) / (1000 * 60 * 60 * 24))
    )
  }
  const experimentMonthlySessions = (expSessions / daysRunning) * 30
  if (experimentMonthlySessions <= 0) return null

  return {
    projectedMonthlyGain: liftAbsolute * experimentMonthlySessions * treatmentAOV,
    liftAbsolute,
    bestVariant: winnerKey,
    treatmentAOV,
    experimentMonthlySessions,
    usedStoreAOVFallback,
  }
}


// --- Attention Queue ---

export type AttentionType =
  | 'srm_warning'
  | 'guardrail_veto'
  | 'guardrail_escalate'
  | 'winner_ready'
  | 'low_traffic'
  | 'underperformer'
  | 'stale'

export interface AttentionItem {
  type: AttentionType
  priority: number
  experiment: Experiment
  results?: any
  // Key metrics for display
  liftPercentage?: number
  confidence?: number
  samplePercentage?: number
  daysSince?: number
  daysRunning?: number
  srmWarning?: string
  /** guardrail_* items: does the purchase signal favour the hypothesis arm? */
  guardrailHypothesisBetter?: boolean
}

// For negative experiments the hypothesis is control and baseline is treatment,
// so the relative lift must be recomputed against the treatment rate (the
// denominator changes — sign-flipping the raw percentage is wrong).
function hypothesisLiftFromRates(
  controlRate: number | undefined,
  treatmentRate: number | undefined,
  rawLiftPct: number | undefined,
  isNegative: boolean
): number | undefined {
  if (!isNegative) return rawLiftPct
  const c = controlRate ?? 0
  const t = treatmentRate ?? 0
  if (t > 0) return ((c - t) / t) * 100
  return rawLiftPct != null ? -rawLiftPct : undefined
}

/**
 * The ONE confidence number for an experiment: the BB+ROPE posterior tail the verdict rests on
 * (max of p_better / p_worse), falling back to the z-test confidence for pre-BB payloads. Shared by
 * the dashboard card, the attention queue and the results page so they can't show different numbers.
 */
export function headlineConfidenceOf(results: any): number {
  if (!results) return 0
  // Goal ladder: when purchases made the call, the confidence IS the purchase posterior (the proxy's
  // own number may even point the other way — Video: cart_view 73.6% for the baseline, purchases
  // 85.7% for the hypothesis).
  if (results.decision_eta?.decided_on === 'guardrail' && results.guardrail?.bb) {
    return Math.max(results.guardrail.bb.p_better ?? 0, results.guardrail.bb.p_worse ?? 0)
  }
  if (results.bb_verdict) {
    return Math.max(results.bb_p_better ?? 0, results.bb_p_worse ?? 0)
  }
  return results.confidence ?? 0
}

/** Which rung decided (or is deciding) — drives which series the dashboard trend follows. */
export function decidingMetricOf(results: any): 'goal' | 'purchase' {
  return results?.decision_eta?.decided_on === 'guardrail' ? 'purchase' : 'goal'
}

/** Confidence bar the verdict is called at (0.95 / 0.85 / 0.70 per risk level); 0.95 for legacy payloads. */
export function confidenceTargetOf(results: any): number {
  return results?.bb_threshold ?? 0.95
}

/** Posterior probability that the HYPOTHESIS arm is better (control in a negative experiment). */
export function hypothesisProbabilityOf(results: any, isNegative: boolean): number {
  if (!results) return 0.5
  if (results.bb_verdict) {
    const pTreatmentBetter = results.bb_p_better ?? 0.5
    const pControlBetter = results.bb_p_worse ?? 0.5
    return isNegative ? pControlBetter : pTreatmentBetter
  }
  // z-test fallback: confidence in the observed direction
  const conf = results.confidence ?? 0
  const treatmentAhead = (results.lift ?? 0) >= 0
  const hypothesisAhead = isNegative ? !treatmentAhead : treatmentAhead
  return hypothesisAhead ? 0.5 + conf / 2 : 0.5 - conf / 2
}

/** Precision label of a lift estimate (`summary.lift.precision` / `guardrail.lift.precision`). */
export type LiftPrecisionLevel = 'precise' | 'approximate' | 'direction_only' | 'unknown'

/**
 * One rung of the "Racional de la decisión" table. Two criteria per rung, in the HYPOTHESIS frame:
 *  - pLoser: how likely is it that the currently-leading arm is actually materially WORSE (the
 *    opposing posterior tail), judged against riskBar = 1 − threshold.
 *  - lift + ci + precision: how precisely the magnitude is estimated (independent of direction —
 *    a winner can be declared while precision is still 'direction_only').
 */
export interface RationaleRung {
  key: 'goal' | 'purchase'
  label: string
  leaderIsHypothesis: boolean | null
  pLoser: number | null
  riskBar: number
  concluded: boolean
  decides: boolean
  liftAbsolute: number | null
  ci: [number, number] | null
  precision: LiftPrecisionLevel
  /** Raw counts per role (hypothesis / baseline frame) so rationale rows can show data, not prose. */
  counts: {
    hypothesis: { sessions: number; conversions: number } | null
    baseline: { sessions: number; conversions: number } | null
  }
  /** Bounded-downside read of the CI, mapped into the hypothesis frame. */
  asymmetry: { leaderIsHypothesis: boolean; downside: number; upside: number; actionable: boolean } | null
}

function mapAsymmetry(a: any, isNegative: boolean): RationaleRung['asymmetry'] {
  if (!a?.leader) return null
  return {
    leaderIsHypothesis: (a.leader === 'treatment') !== isNegative,
    downside: Math.max(0, -(a.worst_case ?? 0)),
    upside: a.best_case ?? 0,
    actionable: Boolean(a.actionable),
  }
}

/**
 * The CI-asymmetry call for the recommendation layer: when nothing is decided yet but the leading
 * arm's worst plausible case is immaterial while its best case is not, propose implementing the
 * leader. Mechanical — mirrors `assessLiftAsymmetry` in the payload; the ladder still outranks it
 * (a veto/escalation means the purchase rung already spoke).
 */
export interface BoundedDownsideCall {
  winnerArm: 'control' | 'treatment'
  downside: number
  upside: number
}

export function boundedDownsideCallOf(results: any): BoundedDownsideCall | null {
  if (!results?.summary) return null
  if (results.summary.is_significant && results.summary.winner) return null
  const gs = results.guardrail?.state
  if (gs === 'veto' || gs === 'escalate' || results.decision_eta?.state === 'vetoed') return null
  const a = results.summary.lift?.asymmetry
  if (!a?.actionable || !a.leader) return null
  return {
    winnerArm: a.leader,
    downside: Math.max(0, -(a.worst_case ?? 0)),
    upside: a.best_case ?? 0,
  }
}

export function rationaleRungsOf(results: any, isNegative: boolean): RationaleRung[] {
  if (!results?.bb_verdict) return []
  const rungs: RationaleRung[] = []
  const decidedOn = results.decision_eta?.decided_on ?? results.summary?.decided_on ?? null

  // Primary rung (the goal — or purchases themselves when purchase IS the goal).
  const pB = results.bb_p_better
  const pW = results.bb_p_worse
  const hasPosterior = pB != null || pW != null
  const treatmentLeads = (pB ?? 0.5) >= (pW ?? 0.5)
  const sLift = results.summary?.lift
  const ciArr = sLift?.confidence_interval as [number, number] | undefined
  const goalIsPurchase = results.summary?.primary_metric?.kind === 'purchase'
  const hypKey = isNegative ? 'control' : 'treatment'
  const baseKey = isNegative ? 'treatment' : 'control'
  const armCounts = (key: string) =>
    results.sessions?.[key] != null
      ? { sessions: results.sessions[key] ?? 0, conversions: results.conversions?.[key] ?? 0 }
      : null
  rungs.push({
    key: goalIsPurchase ? 'purchase' : 'goal',
    label: results.summary?.primary_metric?.label ?? 'Objetivo',
    leaderIsHypothesis: hasPosterior ? treatmentLeads !== isNegative : null,
    pLoser: hasPosterior ? Math.min(pB ?? 0.5, pW ?? 0.5) : null,
    riskBar: 1 - (results.bb_threshold ?? 0.95),
    concluded: results.bb_verdict === 'treatment_better' || results.bb_verdict === 'control_better',
    decides: decidedOn === 'goal',
    liftAbsolute: sLift?.absolute != null ? (isNegative ? -sLift.absolute : sLift.absolute) : null,
    ci: ciArr ? (isNegative ? [-ciArr[1], -ciArr[0]] : [ciArr[0], ciArr[1]]) : null,
    precision: (sLift?.precision?.level as LiftPrecisionLevel) ?? 'unknown',
    counts: { hypothesis: armCounts(hypKey), baseline: armCounts(baseKey) },
    asymmetry: mapAsymmetry(sLift?.asymmetry, isNegative),
  })

  // Purchase rung (guardrail) — only when it is a distinct rung (bb null ⇒ not applicable).
  const g = results.guardrail
  if (g?.bb) {
    const gTreatLeads = (g.bb.p_better ?? 0.5) >= (g.bb.p_worse ?? 0.5)
    const gci = g.lift?.confidence_interval
    rungs.push({
      key: 'purchase',
      label: 'Compras (guardarraíl)',
      leaderIsHypothesis: gTreatLeads !== isNegative,
      pLoser: Math.min(g.bb.p_better ?? 0.5, g.bb.p_worse ?? 0.5),
      riskBar: 1 - (g.bb.threshold ?? 0.95),
      concluded: g.bb.verdict === 'treatment_better' || g.bb.verdict === 'control_better',
      decides: decidedOn === 'guardrail',
      liftAbsolute: g.lift?.absolute != null ? (isNegative ? -g.lift.absolute : g.lift.absolute) : null,
      ci: gci ? (isNegative ? [-gci.upper, -gci.lower] : [gci.lower, gci.upper]) : null,
      precision: (g.lift?.precision?.level as LiftPrecisionLevel) ?? 'unknown',
      counts: {
        hypothesis: g.counts?.[hypKey] ?? null,
        baseline: g.counts?.[baseKey] ?? null,
      },
      asymmetry: mapAsymmetry(g.lift?.asymmetry, isNegative),
    })
  }
  return rungs
}

export function buildAttentionQueue(
  experiments: Experiment[],
  resultsMap: Map<string, any>
): AttentionItem[] {
  const items: AttentionItem[] = []
  const now = new Date()

  for (const exp of experiments) {
    const results = resultsMap.get(exp.id)
    const isNegative = exp.is_negative === true

    if (exp.status === 'active' && results) {
      const summary = results.summary
      const duration = results.duration
      const ctrlRate = results.conversion_rate?.control
      const trtRate = results.conversion_rate?.treatment

      // SRM Warning
      if (results.srm_analysis && !results.srm_analysis.is_valid) {
        items.push({
          type: 'srm_warning',
          priority: 0,
          experiment: exp,
          results,
          srmWarning: results.srm_analysis.warning,
          daysRunning: duration?.days_running,
        })
        continue // SRM is the most critical — don't add other alerts for same experiment
      }

      // Goal ladder: the purchase guardrail outranks the proxy's own verdict (GOAL-LADDER-SPEC.md).
      const guardrail = results.guardrail
      if (guardrail?.state === 'veto' || guardrail?.state === 'escalate') {
        const pPurchase = Math.max(guardrail.bb?.p_better ?? 0, guardrail.bb?.p_worse ?? 0)
        const hypBetter = (guardrail.direction === 'treatment_better') === !isNegative
        items.push({
          type: guardrail.state === 'veto' ? 'guardrail_veto' : 'guardrail_escalate',
          priority: guardrail.state === 'veto' ? 0.5 : 1,
          experiment: exp,
          results,
          confidence: pPurchase,
          liftPercentage: hypothesisLiftFromRates(ctrlRate, trtRate, summary?.lift?.percentage, isNegative),
          daysRunning: duration?.days_running,
          guardrailHypothesisBetter: hypBetter,
        })
        continue
      }

      // Headline confidence = the BB+ROPE posterior the verdict rests on (same number the results
      // page shows), NOT the z-test `confidence` — the two disagreed on every card.
      const headlineConfidence = headlineConfidenceOf(results)
      // Probability that the HYPOTHESIS arm is better (control in a negative experiment).
      const hypothesisProb = hypothesisProbabilityOf(results, isNegative)

      // Winner Ready
      if (summary?.is_significant && summary?.winner) {
        const isReady = summary.auto_winner_requirements?.winner_ready
        if (isReady) {
          items.push({
            type: 'winner_ready',
            priority: 1,
            experiment: exp,
            results,
            liftPercentage: hypothesisLiftFromRates(ctrlRate, trtRate, summary.lift?.percentage, isNegative),
            confidence: headlineConfidence,
            daysRunning: duration?.days_running,
          })
          continue
        }
      }

      const daysRunning = duration?.days_running ?? 0

      // Low Traffic
      if (daysRunning >= 7 && summary?.current_sample_percentage < 30) {
        items.push({
          type: 'low_traffic',
          priority: 2,
          experiment: exp,
          results,
          samplePercentage: summary.current_sample_percentage,
          daysRunning,
        })
        continue
      }

      // Underperformer — evaluate from the hypothesis perspective so negative
      // experiments only get flagged when the hypothesis (control) is losing.
      const hypLift = hypothesisLiftFromRates(ctrlRate, trtRate, summary?.lift?.percentage, isNegative)
      const hypIsNegative = isNegative
        ? (hypLift != null && hypLift < 0)
        : results.lift < 0
      // Flag when the hypothesis is trailing (lift < 0) or the posterior gives it < 50% — BB terms,
      // not the z-test `confidence < 0.5` this used to read.
      if (daysRunning >= 14 && (hypIsNegative || hypothesisProb < 0.5)) {
        items.push({
          type: 'underperformer',
          priority: 3,
          experiment: exp,
          results,
          liftPercentage: hypLift,
          confidence: headlineConfidence,
          daysRunning,
        })
        continue
      }
    }

    // Completed with winner (not yet acted on)
    if (exp.status === 'completed' && exp.results_summary?.is_significant && exp.results_summary?.winner) {
      const variants = exp.results_summary.variants || {}
      const ctrlRate = variants.control?.conversion_rate
      const trtRate = variants.treatment?.conversion_rate
      items.push({
        type: 'winner_ready',
        priority: 1,
        experiment: exp,
        liftPercentage: hypothesisLiftFromRates(ctrlRate, trtRate, exp.results_summary.lift?.percentage, isNegative),
        confidence: undefined, // not stored in results_summary
        daysRunning: undefined,
      })
    }

    // Stale paused experiments
    if (exp.status === 'paused') {
      const daysSince = daysBetween(exp.updated_at, now)
      if (daysSince >= 7) {
        items.push({
          type: 'stale',
          priority: 4,
          experiment: exp,
          daysSince,
        })
      }
    }
  }

  // Sort by priority, then by daysRunning descending
  items.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority
    return (b.daysRunning ?? b.daysSince ?? 0) - (a.daysRunning ?? a.daysSince ?? 0)
  })

  return items
}

// --- Trend computation ---

/**
 * Direction of the HYPOTHESIS's lead over the last 3 days, on the goal series when present.
 * `isNegative` flips the gap so "up" always means "the change under test is pulling ahead".
 */
export function computeTrend(
  timeSeries: any[] | undefined,
  isNegative: boolean = false,
  metric: 'goal' | 'purchase' = 'goal'
): 'up' | 'down' | 'flat' {
  if (!timeSeries || timeSeries.length < 3) return 'flat'

  const recent = timeSeries.slice(-3)
  // `<arm>_rate` is the purchase series; `<arm>_goal_rate` the proxy's. Follow the rung that decides.
  const rate = (d: any, arm: 'control' | 'treatment') =>
    metric === 'purchase'
      ? (d[`${arm}_rate`] ?? 0)
      : (d[`${arm}_goal_rate`] ?? d[`${arm}_rate`] ?? 0)
  const gap = (d: any) => {
    const raw = rate(d, 'treatment') - rate(d, 'control')
    return isNegative ? -raw : raw
  }

  const first = gap(recent[0])
  const avg = (gap(recent[1]) + gap(recent[2])) / 2

  const diff = avg - first
  const threshold = 0.005 // 0.5pp change in the lift gap

  if (diff > threshold) return 'up'
  if (diff < -threshold) return 'down'
  return 'flat'
}
